import { getProductConfig } from '@/app/config/products.config';
import { proposalsService } from '@/services/proposals';
import { businessQuotesService } from '@/services/quotes/business';
import { homeownerQuotesService } from '@/services/quotes/homeowner';
import { STALE_TIME_FIVE_MINUTES_MS } from '@/shared/constants/query-stale-time.constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type PolicyDocument = { key: string; url: string };

type ResolveVariables = {
  policyId: string;
  productCode?: string | null;
};

const QUOTE_QUERY_KEY = 'policy-quote-document';
const PROPOSAL_QUERY_KEY = 'policy-proposal-document';

function isBusinessProduct(productCode?: string | null): boolean {
  return getProductConfig(productCode ?? '')?.proposalService === 'business';
}

/**
 * O documento da cotação vive em rotas diferentes por produto:
 * empresarial em `/portal/v1/business/quotes/:policyId/documents?type=quote`
 * e residencial em `/portal/v1/homeowner/quotes/:policyId/documents`.
 * Retorna `null` (em vez de lançar) quando o anexo ainda não existe — nesse
 * caso o PDF é gerado sob demanda no clique do botão.
 */
async function fetchQuoteDocument(
  policyId: string,
  productCode?: string | null
): Promise<PolicyDocument | null> {
  const response = isBusinessProduct(productCode)
    ? await businessQuotesService.getDocument(policyId, 'quote')
    : await homeownerQuotesService.getInspectionDocument(policyId);

  return response.success && response.data?.url ? response.data : null;
}

async function fetchProposalDocument(
  policyId: string,
  productCode?: string | null
): Promise<PolicyDocument | null> {
  const response = await proposalsService.getProposalDocument(
    policyId,
    productCode
  );

  return response.success && response.data?.url ? response.data : null;
}

export function usePolicyQuoteDocument(
  policyId: string | undefined,
  productCode?: string | null
) {
  return useQuery<PolicyDocument | null>({
    queryKey: [QUOTE_QUERY_KEY, policyId, productCode ?? ''],
    queryFn: () => fetchQuoteDocument(policyId as string, productCode),
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
    enabled: !!policyId,
    retry: false,
  });
}

export function usePolicyProposalDocument(
  policyId: string | undefined,
  productCode?: string | null
) {
  return useQuery<PolicyDocument | null>({
    queryKey: [PROPOSAL_QUERY_KEY, policyId, productCode ?? ''],
    queryFn: () => fetchProposalDocument(policyId as string, productCode),
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
    enabled: !!policyId,
    retry: false,
  });
}

/**
 * Resolve o PDF da cotação: usa o anexo já persistido e, se não houver,
 * gera via `POST /portal/v1/quotes/:policyId/pdf` (`stage=quote`), que
 * também salva o anexo no S3/file_attachments.
 *
 * O empresarial cai sempre nesse fallback: `initiate-proposal` ignora a
 * geração do PDF da cotação para o produto 180007.
 */
export function useResolvePolicyQuoteDocument() {
  const queryClient = useQueryClient();

  return useMutation<PolicyDocument, Error, ResolveVariables>({
    mutationKey: ['policies', 'resolve-quote-document'],
    mutationFn: async ({ policyId, productCode }) => {
      const existing = await fetchQuoteDocument(policyId, productCode);
      if (existing) return existing;

      if (!productCode) {
        throw new Error('Código do produto não encontrado para gerar o PDF.');
      }

      const generated = await homeownerQuotesService.getProposalPdf({
        policyId,
        productCode,
        stage: 'quote',
      });

      if (!generated.success || !generated.data?.url) {
        throw new Error(generated.error || 'Erro ao gerar PDF da cotação');
      }

      return generated.data;
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEY, variables.policyId],
      });
    },
  });
}

/**
 * Resolve o PDF da proposta: usa o anexo já persistido e, se não houver,
 * gera via `POST /portal/v1/{produto}/proposals/:policyId/pdf`
 * (`stage=proposal`), que também persiste o anexo.
 *
 * O empresarial cai sempre nesse fallback: `transmit-proposal` ignora a
 * geração do PDF da proposta para o produto 180007.
 */
export function useResolvePolicyProposalDocument() {
  const queryClient = useQueryClient();

  return useMutation<PolicyDocument, Error, ResolveVariables>({
    mutationKey: ['policies', 'resolve-proposal-document'],
    mutationFn: async ({ policyId, productCode }) => {
      const existing = await fetchProposalDocument(policyId, productCode);
      if (existing) return existing;

      if (!productCode) {
        throw new Error('Código do produto não encontrado para gerar o PDF.');
      }

      const generated = await proposalsService.generatePdf(
        policyId,
        { productCode, stage: 'proposal' },
        productCode
      );

      if (!generated.success || !generated.data?.url) {
        throw new Error(generated.error || 'Erro ao gerar PDF da proposta');
      }

      return generated.data;
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [PROPOSAL_QUERY_KEY, variables.policyId],
      });
    },
  });
}
